import React, { useState, useEffect } from 'react';
import { Play, X, Sun, Cloud, Clock, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from './ui/card';

const VideoShowcase = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getTimeForOffset = (offset) => {
    const utc = currentTime.getTime() + (currentTime.getTimezoneOffset() * 60000);
    const cityTime = new Date(utc + (3600000 * offset));
    return cityTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  };

  const zones = [
    { city: 'Los Angeles', tz: 'PST', offset: -8, flag: '🇺🇸' },
    { city: 'New York', tz: 'EST', offset: -5, flag: '🇺🇸' },
    { city: 'London', tz: 'GMT', offset: 0, flag: '🇬🇧' },
    { city: 'Dubai', tz: 'GST', offset: 4, flag: '🇦🇪' },
    { city: 'Tokyo', tz: 'JST', offset: 9, flag: '🇯🇵' },
    { city: 'Sydney', tz: 'AEDT', offset: 11, flag: '🇦🇺' },
  ];

  return (
    <section className="py-20 px-4 bg-gradient-to-br from-white via-gray-50 to-yellow-50 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0 opacity-30">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-red-400 rounded-full"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0.2, 0.8, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 2 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto max-w-6xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-yellow-600 font-semibold mb-4">Tour Preview</p>
          <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Experience <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-yellow-500">Hollywood Magic</span>
          </h2>
          <p className="text-base md:text-lg text-gray-500 max-w-2xl mx-auto">Watch what a day on the #1 rated Hollywood sightseeing tour looks like</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="relative"
        >
          <Card className="overflow-hidden border border-yellow-500/30 shadow-[0_0_30px_rgba(212,175,55,0.1)]">
            <div className="relative aspect-video bg-gradient-to-br from-gray-200 to-gray-300">
              {/* Video Thumbnail */}
              <img 
                src="https://images.unsplash.com/photo-1594663805807-29a7cc1847c0?auto=format&fit=crop&w=1200&q=80"
                alt="Hollywood Tour Preview"
                className="w-full h-full object-cover"
              />
              
              {/* Play Button Overlay */}
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <motion.button
                  onClick={() => setIsPlaying(true)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black rounded-full p-8 shadow-[0_0_30px_rgba(212,175,55,0.4)] hover:shadow-[0_0_50px_rgba(212,175,55,0.6)] transition-all"
                >
                  <Play className="h-12 w-12 fill-black text-black" />
                </motion.button>
              </div>

              {/* Floating Elements */}
              <motion.div
                className="absolute top-4 left-4 bg-white/90 backdrop-blur-md rounded-full px-4 py-2 text-gray-900 text-sm font-bold border border-yellow-500/30"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                ⭐ 2 Min Preview
              </motion.div>
            </div>
          </Card>

          {/* Video Modal */}
          <AnimatePresence>
            {isPlaying && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
                onClick={() => setIsPlaying(false)}
              >
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.8 }}
                  className="relative w-full max-w-5xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setIsPlaying(false)}
                    className="absolute -top-12 right-0 text-white hover:text-red-500 transition-colors"
                  >
                    <X className="h-8 w-8" />
                  </button>
                  <div className="aspect-video bg-black rounded-lg overflow-hidden">
                    <video
                      autoPlay
                      controls
                      className="w-full h-full object-cover"
                    >
                      <source src="https://customer-assets.emergentagent.com/job_0db38a4d-9eca-46f0-9975-4175ea5b16fd/artifacts/ij855tvl_IMG_0224.MP4" type="video/mp4" />
                    </video>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

      </div>
    </section>
  );
};

export default VideoShowcase;
